<template>
  <div class="bankid-view text-left">
    <div class="bankid-view__title q-mt-lg flex row justify-between">
      <div class="col-1">
        <ws-button
          class="text-ws-ui-primary q-mr-md"
          icon="arrow_left"
          :aria-label="$t('general.accessibility.button.back_button')"
          @click="goBack"
        />
      </div>
      <div class="col-10">
        <h5>{{ $t('bank_id.create_new_user.title') }}</h5>
        <p id="qr_description" class="small">
          {{ $t('bank_id.create_new_user.description') }}
        </p>
      </div>
    </div>
    <div class="bankid-view__content column justify-center q-mt-lg">
      <div class="bankid-view__qrcode text-center">
        <div v-if="!loading && !allowRepeat" class="qr-code-wrapper q-mb-md">
          <ws-qr-code
            aria-labelledby="qr_description"
            :aria-label="$t('general.accessibility.qr_code')"
            :content="qrCode"
          />
        </div>
        <ws-button
          v-if="!loading && !allowRepeat"
          :aria-label="$t('bank_id.accessibility.open_bank_id_application')"
          :type="WsButtonEnum.Type.TERTIARY_NEUTRAL"
          icon="restore"
          :size="WsButtonEnum.Size.MEDIUM"
          @click="openInApp"
        >
          {{ $t('bank_id.create_new_user.open_in_app') }}
        </ws-button>
      </div>
      <div class="bankid-view__qr-error text-center">
        <ws-button
          v-if="!loading && allowRepeat"
          :aria-label="$t('bank_id.accessibility.start_again')"
          :type="WsButtonEnum.Type.TERTIARY_NEUTRAL"
          icon="restore"
          :size="WsButtonEnum.Size.MEDIUM"
          @click="initiateQrCode"
        >
          {{ $t('bank_id.create_new_user.qr_error_button') }}
        </ws-button>
        <ws-spinner v-if="loading" :alt="$t('general.accessibility.loading')" />
      </div>
    </div>
    <div class="q-my-lg">
      <ws-delimiter text="wsk.general.or" alt="" aria-hidden="true" />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onBeforeMount, ref } from 'vue';
import { WsButton, WsButtonEnum, WsQrCode, WsSpinner } from '@loopia-group/components-vue2';
import { beginBankIdSession, resolveBankIdSession } from '@/services/cart/cart-api.service.ts';
import WsDelimiter from '@WS_Components/WsDelimiter.vue';
import { mobile } from '@loopia-group/vue2-utils';

export default defineComponent({
  name: 'BankIdCreateProfile',
  components: {
    WsDelimiter,
    WsSpinner,
    WsQrCode,
    WsButton
  },
  props: {

  },
  emits: ['goBack'],
  // eslint-disable-next-line max-lines-per-function
  setup(props, { emit }) {
    const loading = ref<boolean>(false);
    const orderRefId = ref<string|null>(null);
    const onDeviceAppAuth = ref<boolean>(false);
    const beginResponseData = ref(null);
    const initResponseData = ref(null);
    const qrCode = ref<string>('');
    const status = ref<string>('');
    const allowRepeat = ref<boolean>(false);
    const interval = ref(null);
    const qrErrors = ref([]);
    const token = ref<string>('');
    //     const hasErrors = computed(() => !!qrErrors.value.length);

    const STATUS_PENDING = 'pending';
    const STATUS_FAILED = 'failed';
    const STATUS_COMPLETE = 'complete';

    onBeforeMount(() => {
      // Starting QrCode cycle on first load
      initiateQrCode();
    });

    const initiateQrCode = async () => {
      clearErrors();
      loading.value = true;
      onDeviceAppAuth.value = false;
      // getting OrderRef from first call with onDeviceAppAuth false to begin QrCode cycle
      beginResponseData.value = await beginBankIdSession(onDeviceAppAuth.value);
      orderRefId.value = beginResponseData.value.orderRefId;

      // awaiting second call
      await repeatCall();

      loading.value = false;
      allowRepeat.value = false;
    };
    const repeatCall = async () => {
      try {
        initResponseData.value = await resolveBankIdSession(orderRefId.value);
      } catch (e) {
        console.log(e, 'boohoooo no worky');
        // handleErrors();

        // stopping timer to display error msg and Try again button
        stopTimer();
        allowRepeat.value = true;

        return;
      }

      if (initResponseData.value.qrCode !== null) {
        //null comes from BE in case of inApp workflow
        qrCode.value = initResponseData.value.qrCode;
      }

      status.value = initResponseData.value.status || STATUS_FAILED;
      checkStatus();
    };

    const openInApp = async () => {
      loading.value = true;
      // currently just for desktop device
      // we are stopping timer for previous QrCode call to create new one for InApp workflow.
      stopTimer();
      try {
        // getting OrderRef from first call with onDeviceAppAuth true to begin InApp cycle
        onDeviceAppAuth.value = true;
        beginResponseData.value = await beginBankIdSession(onDeviceAppAuth.value);
        orderRefId.value = beginResponseData.value.orderRefId;
        // second call to get Token
        await repeatCall();
      } catch (e) {
        console.log(e, 'boohoooo no worky');
        // handleErrors();
        // stopping timer to display error msg and Try again button
        stopTimer();
        allowRepeat.value = true;
        return;
      }
      token.value = beginResponseData.value.autoStartToken;
      // opening app
      window.location = encodeURI(`bankid:///?autostarttoken=${token.value}&redirect=null`);

      checkStatus();
    };

    const startTimer = () => {
      interval.value = setInterval(() => repeatCall(), 2000);
    };

    const checkStatus = () => {

      if (status.value === STATUS_COMPLETE) {
        stopTimer();
        console.log('successfully verified');
      } else if (status.value === STATUS_PENDING) {
        if (interval.value === null) {
          startTimer();
        }
      } else if (status.value === STATUS_FAILED) {
        stopTimer();
        allowRepeat.value = true;
        //  .catch(catchCollectError);
        // @ts-ignore
        // qrErrors.value.push({translation: (`bank_id.message.${data.statusMessage || 'RFA22'}`)});
      }
    };

    const stopTimer = () => {
      // clearing last QrCode and interval value for Try again button to start cycle again.
      clearInterval(interval.value);
      qrCode.value = null;
      interval.value = null;
    };

    const clearErrors = () => {
      qrErrors.value = [];
    };

    const goBack = () => {
      emit('goBack');
    };

    return {
      goBack,
      loading,
      initiateQrCode,
      qrCode,
      openInApp,
      allowRepeat,
    };
  },
  computed: {
    mobile() {
      return mobile;
    },
    WsButtonEnum() {
      return WsButtonEnum;
    }
  },
  methods: {
  },
});

//TODO
// catch classic errors
// map bankId errors to translations
// slap errors into flash message
// tests

// inspiration for error handling from auth, will be implemented in next ticket
//     const catchCollectError = (error: any) => {
//       const message = (error.response.data.type === '/bankid/personal-number-not-found')
//         ? 'bank_id.number_not_assigned'
//         : 'bank_id.message.RFA5'; // Internal error
//
//       const errors = opening.value
//         ? inputErrors.value
//         : qrErrors.value;
//
//       // @ts-ignore
//       errors.push({translation: t(message)});
//
//       opening.value = false;
//       loading.value = false;
//       qrContents.value = '';
//     };
//
//     const handleErrors = (error: any) => {
//       nextTick(() => {
//         const errors = window.location.hash ? inputErrors.value : qrErrors.value;
//
//         let errorType = error?.response?.data?.type || null;
//
//         if (errorType) {
//           if (!errorType.startsWith('RF')) {
//             errorType = 'RFA22';
//           }
//
//           // @ts-ignore
//           errors.push({translation: t(`bank_id.message.${errorType}`)});
//         } else {
//           // @ts-ignore
//           errors.push({translation: t('bank_id.message.RFA5')}); // Internal error
//           // this.errorService.standardApiErrorProcess(this);
//         }
//       });
//     };
//   };
</script>
<style lang="scss" scoped>
.bankid-view {
  &__content {
    min-height: 200px;
  }
}
</style>
